export const LEGAL_DOCUMENT_IDS = ['terms', 'privacy', 'customer'] as const;

export type LegalDocumentId = (typeof LEGAL_DOCUMENT_IDS)[number];

export const PUBLIC_PRIVACY_URL = 'https://simaizeer.org/privacy.html';
export const PUBLIC_TERMS_URL = 'https://simaizeer.org/terms.html';
export const PUBLIC_CUSTOMER_TERMS_URL = 'https://simaizeer.org/customer-terms.html';

export const legalConfig = {
  productName: 'Simaizeer',
  platformName: 'ShahAgent',
  operatorLegalName: 'Simaizeer',
  operatorAddress: 'Baku, Azerbaijan',
  privacyEmail: 'privacy@simaizeer.ai',
  legalEmail: 'legal@simaizeer.ai',
  supportEmail: 'support@simaizeer.ai',
  governingLawAz: 'Azərbaycan Respublikası',
  governingLawEn: 'the Republic of Azerbaijan',
  effectiveDateAz: '17 sentyabr 2026',
  effectiveDateEn: '17 September 2026',
  connectedChannelsEn:
    'WhatsApp, Instagram, Facebook Messenger, Telegram, TikTok, Signal, phone, email, web chat, and any additional channel enabled for the workspace',
  connectedChannelsAz:
    'WhatsApp, Instagram, Facebook Messenger, Telegram, TikTok, Signal, telefon, e-poçt, veb çat və iş sahəsində aktivləşdirilmiş hər hansı əlavə kanal',
  messageSurfacesEn:
    'direct messages, public and private comments, comments on stories, reels, posts and live broadcasts, internal direct messages and team channels, scheduled outbound messages, and media, stickers and location',
  messageSurfacesAz:
    'birbaşa mesajlar, ictimai və şəxsi şərhlər, hekayə, reel, post və canlı yayımlardakı şərhlər, daxili birbaşa mesajlar və komanda kanalları, planlaşdırılmış çıxış mesajları, həmçinin media, stiker və məkan',
  platformProvidersEn:
    'Meta (WhatsApp, Instagram and Facebook Messenger), Telegram, TikTok, Signal and other providers of connected channels',
  platformProvidersAz:
    'Meta (WhatsApp, Instagram və Facebook Messenger), Telegram, TikTok, Signal və qoşulmuş kanalların digər təminatçıları',
  workspaceModulesEn:
    'contacts and leads, notes, tasks, calendar, reminders, field check-ins, automations (including interactive buttons and lists), spam classification, AI text and voice assistance, the share extension, knowledge, products and invoices',
  workspaceModulesAz:
    'kontaktlar və lidlər, qeydlər, tapşırıqlar, təqvim, xatırlatmalar, sahə yoxlamaları, avtomatlaşdırmalar (interaktiv düymələr və siyahılar daxil olmaqla), spam təsnifatı, AI mətn və səs köməyi, paylaşım uzantısı, bilik bazası, məhsullar və fakturalar',
} as const;

const DOCUMENT_FILES: Record<LegalDocumentId, string> = {
  terms: 'terms-of-service.md',
  privacy: 'privacy-policy.md',
  customer: 'customer-terms.md',
};

const DOCUMENT_ALIASES: Record<string, LegalDocumentId> = {
  terms: 'terms',
  'terms-of-service': 'terms',
  tos: 'terms',
  privacy: 'privacy',
  'privacy-policy': 'privacy',
  customer: 'customer',
  'customer-terms': 'customer',
};

export const parseLegalDocumentId = (value: string | undefined): LegalDocumentId | null => {
  if (!value) {
    return null;
  }

  return DOCUMENT_ALIASES[value.trim().toLowerCase()] ?? null;
};

export const legalDocumentFileName = (id: LegalDocumentId): string => DOCUMENT_FILES[id];

export const legalAssetLanguage = (language: string): 'az' | 'en' =>
  language.toLowerCase().startsWith('az') ? 'az' : 'en';

export const applyLegalPlaceholders = (markdown: string, language: string): string => {
  const az = legalAssetLanguage(language) === 'az';
  const values: Record<string, string> = {
    product: legalConfig.productName,
    platform: legalConfig.platformName,
    operator: legalConfig.operatorLegalName,
    operator_address: legalConfig.operatorAddress,
    privacy_email: legalConfig.privacyEmail,
    legal_email: legalConfig.legalEmail,
    support_email: legalConfig.supportEmail,
    governing_law: az ? legalConfig.governingLawAz : legalConfig.governingLawEn,
    effective_date: az ? legalConfig.effectiveDateAz : legalConfig.effectiveDateEn,
    connected_channels: az ? legalConfig.connectedChannelsAz : legalConfig.connectedChannelsEn,
    message_surfaces: az ? legalConfig.messageSurfacesAz : legalConfig.messageSurfacesEn,
    platform_providers: az ? legalConfig.platformProvidersAz : legalConfig.platformProvidersEn,
    workspace_modules: az ? legalConfig.workspaceModulesAz : legalConfig.workspaceModulesEn,
  };

  return markdown.replace(/\{\{(\w+)\}\}/g, (match, key: string) => values[key] ?? match);
};

export const legalDocumentUrl = (id: LegalDocumentId, language = 'en'): string =>
  `/legal/${legalAssetLanguage(language)}/${legalDocumentFileName(id)}`;
